import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Reoverlay } from "reoverlay";
import { styled } from "styled-components";
import GenericModal from "../GenericModal/GenericModal";
import { Container } from "../../styledComponents/Container/Container";
import Button from "../../styledComponents/CustomButtons/Buttons";
import { useAppSelector } from "../../store/hooks";
import { CONTRAST_ENUM } from "../../pages/SettingsPage/constants/constants";
import { getContrastTextColor } from "../../utils/glassmorphismUtils";
import {
  getA2hsPlatform,
  isIosSafari,
} from "../../utils/a2hs/a2hsDetection";
import { promptNativeInstall } from "../../utils/a2hs/a2hsInstallPrompt";
import { setA2hsPromptStatus } from "../../utils/a2hs/a2hsStorage";

const Title = styled.h1<{ contrast: CONTRAST_ENUM }>`
  color: ${({ contrast }) => getContrastTextColor(contrast)};
  line-height: 1.2;
  text-align: center;
  font-size: 1.35rem;
`;

const Description = styled.p<{ contrast: CONTRAST_ENUM }>`
  color: ${({ contrast }) => getContrastTextColor(contrast)};
  text-align: center;
  margin: 0;
  line-height: 1.4;
`;

const AddToHomeScreenModal = (): JSX.Element => {
  const { t } = useTranslation("common");
  const contrast = useAppSelector((state) => state.userInfo.contrast);
  const platform = getA2hsPlatform();
  const iosSafari = isIosSafari();
  const isIos = platform === "ios";
  const [isInstalling, setIsInstalling] = useState(false);
  const primaryVariant =
    contrast === CONTRAST_ENUM.WHITE ? "primary" : "secondary";

  const handleDismiss = () => {
    setA2hsPromptStatus("dismissed");
    Reoverlay.hideModal();
  };

  const handleClose = () => {
    setA2hsPromptStatus("accepted");
    Reoverlay.hideModal();
  };

  const handleAdd = async () => {
    if (platform === "android" || platform === "other") {
      setIsInstalling(true);
      const outcome = await promptNativeInstall();
      setIsInstalling(false);

      if (outcome === "accepted") {
        setA2hsPromptStatus("accepted");
        Reoverlay.hideModal();
        return;
      }

      if (outcome === "dismissed") {
        setA2hsPromptStatus("dismissed");
        Reoverlay.hideModal();
        return;
      }
    }

    handleClose();
  };

  const description = isIos
    ? iosSafari
      ? t("a2hs.descriptionIosSafari")
      : t("a2hs.descriptionIosOtherBrowser")
    : t("a2hs.description");

  const buttonLabel = isIos ? t("a2hs.gotIt") : t("a2hs.add");

  return (
    <GenericModal size="sm" onCancel={handleDismiss}>
      <Container
        flexDirection="column"
        gap="1.25rem"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        <Title contrast={contrast}>{t("a2hs.title")}</Title>
        <Description contrast={contrast}>{description}</Description>
        <Button
          onClick={isIos ? handleClose : handleAdd}
          variant={primaryVariant}
          isLoading={isInstalling}
          disabled={isInstalling}
        >
          {buttonLabel}
        </Button>
      </Container>
    </GenericModal>
  );
};

export default AddToHomeScreenModal;
